import { useState } from 'react'
import { Alert, Badge, Group, Pagination, Skeleton, Text } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { securityApi } from '@/api/security'
import { formatApiError } from '@/api/errors'
import type { SecurityFinding } from '@/api/security'
import { PriorityQueue, PriorityQueueHeader } from './PriorityQueue'

export const TAMANHO_DA_PAGINA = 25

interface FilaDeCorrecaoProps {
  tenantId: number
  /** Categoria OWASP clicada no heatmap; null = fila do tenant inteiro. */
  categoria: string | null
  onLimparCategoria: () => void
  onSelect: (finding: SecurityFinding) => void
}

/** Fila de correção paginada no SERVIDOR: abertos, pior severidade e mais
 *  antigo primeiro. Antes parava nos 50 primeiros da categoria e nos 20
 *  crít+alto do tenant — o A05 com 103 abertos não passava do 50º (21/10/2026). */
export function FilaDeCorrecao({ tenantId, categoria, onLimparCategoria, onSelect }: FilaDeCorrecaoProps) {
  const [pagina, setPagina] = useState(1)
  // Trocar de categoria volta à página 1 já neste render — com useEffect, o
  // render intermediário pediria o offset antigo da categoria nova.
  const [categoriaDaPagina, setCategoriaDaPagina] = useState(categoria)
  if (categoriaDaPagina !== categoria) {
    setCategoriaDaPagina(categoria)
    setPagina(1)
  }

  const offset = (pagina - 1) * TAMANHO_DA_PAGINA
  const fila = useQuery({
    queryKey: ['security', 'fila', tenantId, categoria, offset],
    queryFn: () => securityApi.listFindings(tenantId, {
      category: categoria ?? undefined, aberto: true, limit: TAMANHO_DA_PAGINA, offset,
    }),
    placeholderData: (anterior) => anterior,
  })

  const findings = fila.data?.findings ?? []
  const total = fila.data?.total ?? 0
  const paginas = Math.ceil(total / TAMANHO_DA_PAGINA)

  return (
    <>
      <PriorityQueueHeader offset={offset} shown={findings.length} total={total} />
      {categoria && (
        <Group mb="sm">
          <Badge
            rightSection={<IconX size={12} />}
            style={{ cursor: 'pointer' }}
            variant="light"
            color="teal"
            onClick={onLimparCategoria}
          >
            {categoria}
          </Badge>
          <Text size="xs" c="dimmed">filtrando a fila por categoria</Text>
        </Group>
      )}
      {fila.isError ? (
        <Alert color="red">{formatApiError(fila.error, 'Falha ao carregar a fila de correção')}</Alert>
      ) : fila.isLoading ? (
        <Skeleton height={120} />
      ) : (
        <PriorityQueue findings={findings} category={categoria} onSelect={onSelect} />
      )}
      {paginas > 1 && (
        <Group justify="flex-start" mt="md">
          <Pagination value={pagina} total={paginas} onChange={setPagina} size="sm" />
        </Group>
      )}
    </>
  )
}
